import {
  associatePerformanceService,
  associateScorecardService,
  clientConnectsService,
  stylistManagerClientConnectsService,
} from 'config/api';
import actionTypes from './actionTypes';
import { adjustPerformanceParams } from '../utils';

export const getMetrics =
  (associatePin, options = {}) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.PERF_DATA_LOADING,
    });

    const params = adjustPerformanceParams(options);

    try {
      const response = await associatePerformanceService.post(`v4/data/${associatePin}/metrics`, params);
      dispatch({ type: actionTypes.PERFORMANCE_DATA_SUCCESS, payload: response.data });
    } catch (error) {
      dispatch({ type: actionTypes.PERF_DATA_ERROR, payload: error });
    }
  };

export const clearMetricsData = () => ({
  type: actionTypes.PERFORMANCE_DATA_RESET,
});

export const getTodaysSalesForAssociate =
  (associatePin, version = 'v2', managerStoreId = '') =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.TODAYS_SALES_LOADING,
    });
    const storeId = version === 'v3' && managerStoreId ? `?storeId=${parseInt(managerStoreId, 10)}` : '';
    const storeNumber = managerStoreId && { storeId: managerStoreId };
    try {
      const url = `/${version}/data/${associatePin}/sales${storeId}`;
      const res = await associatePerformanceService.get(url);
      dispatch({ type: actionTypes.TODAYS_SALES_SUCCESS, payload: { data: res.data, ...storeNumber } });
    } catch (error) {
      dispatch({ type: actionTypes.TODAYS_SALES_ERROR, payload: error });
    }
  };

export const getSalesForManager =
  ({ managerSalesQuery, storeId, isTreeModeEnabled, divisionsMasterList }) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.MANAGER_SALES_LOADING,
    });

    try {
      const { type } = managerSalesQuery;
      const isAssociateSales = type === 'ASSOCIATES' || type === 'MY_TEAM';
      const associateSales = isAssociateSales ? '/associates' : '';
      const query = storeId ? `?storeId=${parseInt(storeId, 10)}` : '';
      const apiVersion = isAssociateSales ? 'v1' : 'v2';

      const {
        data: { items, count },
      } = await associatePerformanceService.post(
        `/${apiVersion}/manager/sales/metrics${associateSales}${query}`,
        managerSalesQuery,
      );

      const payload = {
        dataType: managerSalesQuery.type,
        items,
        count,
        divisionsMasterList,
      };

      if (isTreeModeEnabled) {
        dispatch({ type: actionTypes.MANAGER_SALES_TREE_SUCCESS, payload });
      } else {
        dispatch({ type: actionTypes.MANAGER_SALES_SUCCESS, payload });
      }
    } catch (error) {
      dispatch({ type: actionTypes.MANAGER_SALES_ERROR, payload: error });
    }
  };

export const getSalesTreeForManager = (requestParams, storeId, divisionsMasterList) => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_SALES_TREE_LOADING,
  });

  try {
    const query = storeId ? `?storeId=${parseInt(storeId, 10)}` : '';
    const { divisionId, groupId } = requestParams;
    const {
      data: { items, count },
    } = await associatePerformanceService.post(`/v1/manager/sales/divisionMetrics/${query}`, requestParams);

    dispatch({
      type: actionTypes.MANAGER_SALES_TREE_SUCCESS,
      payload: { items, count, divisionId, groupId, divisionsMasterList },
    });
  } catch (error) {
    dispatch({ type: actionTypes.MANAGER_SALES_TREE_ERROR, payload: error });
  }
};

export const saveManagerSalesTree = ({ pageId, processedNodeIds, expandedNodesMap, dateFilter }) => ({
  type: actionTypes.MANAGER_SALES_TREE_SAVE_STATE,
  payload: { pageId, processedNodeIds, expandedNodesMap, dateFilter },
});

export const clearManagerSalesTreeCache = () => ({
  type: actionTypes.MANAGER_SALES_TREE_CLEAR_CACHE,
});

export const managerSalesTreeRestore = (pageId) => ({
  type: actionTypes.MANAGER_SALES_TREE_RESTORE,
  payload: { pageId },
});

export const clearSalesTreeForManager = () => ({
  type: actionTypes.MANAGER_SALES_TREE_CLEAR,
});

export const clearManagerSales = () => ({
  type: actionTypes.MANAGER_SALES_CLEAR,
});

export const getManagerSalesDetails = (requestParams, storeId) => async (dispatch) => {
  const requestQuery = storeId ? `?storeId=${parseInt(storeId, 10)}` : '';

  dispatch({ type: actionTypes.MANAGER_SALES_DETAILS_LOADING });

  try {
    const { data } = await associatePerformanceService.post(`/v1/manager/sales/details${requestQuery}`, requestParams);
    dispatch({ type: actionTypes.MANAGER_SALES_DETAILS_SUCCESS, payload: data });
  } catch (error) {
    dispatch({ type: actionTypes.MANAGER_SALES_DETAILS_ERROR });
  }
};

export const getTodaysSalesForAssociateV3 = (associatePin, storeNumber) =>
  getTodaysSalesForAssociate(associatePin, 'v3', storeNumber);

export const getEngagements = (associatePin, timePeriod) => async (dispatch) => {
  dispatch({
    type: actionTypes.ASSOCIATE_ENGAGEMENTS_LOADING,
  });

  try {
    const options = { params: { timePeriod } };
    const { data } = await associateScorecardService.get(`/v1/scorecard/${associatePin}/outreach`, options);
    dispatch({
      type: actionTypes.ASSOCIATE_ENGAGEMENTS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.ASSOCIATE_ENGAGEMENTS_ERROR,
      payload: error,
    });
  }
};

export const getDivisionsPerDesigner =
  ({ designerName, filterType, managerStoreId, dateFrom, dateTo }, divisionsMasterList = {}) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.MANAGER_DIVISIONS_PER_DESIGNER_LOADING,
    });

    try {
      const postBody = {
        type: 'DIVISIONS',
        designerName,
        filterType,
        dateFrom,
        dateTo,
      };

      const { data } = await associatePerformanceService.post(
        `/v1/manager/sales/metrics/designer?storeId=${parseInt(managerStoreId, 10)}`,
        postBody,
      );

      dispatch({
        type: actionTypes.MANAGER_DIVISIONS_PER_DESIGNER_SUCCESS,
        payload: { items: data.items, divisionsMasterList },
      });
    } catch (error) {
      dispatch({
        type: actionTypes.MANAGER_DIVISIONS_PER_DESIGNER_ERROR,
        payload: error,
      });
    }
  };

export const getGroupsPerDesigner =
  ({ designerName, filterType, divisionId, managerStoreId, dateFrom, dateTo }) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.MANAGER_GROUPS_PER_DESIGNER_LOADING,
    });

    try {
      const postBody = {
        type: 'GROUPS',
        designerName,
        divisionId,
        filterType,
        dateFrom,
        dateTo,
      };

      const { data } = await associatePerformanceService.post(
        `/v1/manager/sales/metrics/designer?storeId=${parseInt(managerStoreId, 10)}`,
        postBody,
      );

      dispatch({
        type: actionTypes.MANAGER_GROUPS_PER_DESIGNER_SUCCESS,
        payload: data.items,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.MANAGER_GROUPS_PER_DESIGNER_ERROR,
        payload: error,
      });
    }
  };

export const getClientConnects = (selectedStore) => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_CLIENT_CONNECTS_LOADING,
  });

  try {
    const { storeId } = selectedStore;
    const { data } = await clientConnectsService.get(`/fullMetrics?storeId=${storeId}`);
    dispatch({
      type: actionTypes.MANAGER_CLIENT_CONNECTS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.MANAGER_CLIENT_CONNECTS_ERROR,
      payload: error,
    });
  }
};

export const getClientConnectsDetails = (selectedStore, requestParams) => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_CLIENT_CONNECTS_DETAILS_LOADING,
  });

  try {
    const { storeId } = selectedStore;
    const { data } = await clientConnectsService.post(`/associatesMetrics?storeId=${storeId}`, requestParams);

    dispatch({
      type: actionTypes.MANAGER_CLIENT_CONNECTS_DETAILS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.MANAGER_CLIENT_CONNECTS_DETAILS_ERROR,
      payload: error,
    });
  }
};

export const getIndividualClientConnectsDetails = (storeId, associatePin) => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_LOADING,
  });

  const requestParams = {
    searchText: associatePin,
    sortType: 'desc',
    pagination: {
      from: 1,
      size: 1,
    },
  };

  try {
    const { data } = await clientConnectsService.post(`/associatesMetrics?storeId=${storeId}`, requestParams);

    dispatch({
      type: actionTypes.MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_SUCCESS,
      payload: data?.items[0],
    });
  } catch (error) {
    dispatch({
      type: actionTypes.MANAGER_CLIENT_CONNECTS_DETAILS_ERROR,
      payload: error,
    });
  }
};

export const getStylistManagerIndividualClientConnectsDetails = (managerPin, associatePin) => async (dispatch) => {
  dispatch({
    type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_LOADING,
  });

  const requestParams = {
    searchText: associatePin,
    sortType: 'desc',
    pagination: {
      from: 1,
      size: 1,
    },
  };

  try {
    const { data } = await stylistManagerClientConnectsService.post(
      `/associatesMetrics?managerPin=${managerPin}`,
      requestParams,
    );

    dispatch({
      type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_SUCCESS,
      payload: data?.items[0],
    });
  } catch (error) {
    dispatch({
      type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_ERROR,
      payload: error,
    });
  }
};

export const clearSelectedAssociateDetails = () => ({
  type: actionTypes.MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_CLEAR,
});

export const clearSelectedStylistManagerAssociateDetails = () => ({
  type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_SELECT_ASSOCIATE_CLEAR,
});

export const clearClientConnectsDetails = () => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_CLIENT_CONNECTS_CLEAR,
  });
};

export const getManagerAssociateSalesMetrics = (storeId, requestParams) => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_LOADING,
  });

  try {
    const { data } = await associatePerformanceService.post(
      `v1/manager/sales/associates?storeId=${storeId}`,
      requestParams,
    );
    dispatch({
      type: actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_ERROR,
    });
  }
};

export const clearManagerAssociateSalesMetrics = () => async (dispatch) => {
  dispatch({
    type: actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_CLEAR,
  });
};

export const getTodaysSalesForStylistManager =
  ({ associatePin, version }) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.TODAYS_SALES_FOR_STYLIST_MANAGER_LOADING,
    });
    try {
      const url = `/${version}/data/${associatePin}/stylistsales`;
      const response = await associatePerformanceService.get(url);
      dispatch({
        type: actionTypes.TODAYS_SALES_FOR_STYLIST_MANAGER_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({ type: actionTypes.TODAYS_SALES_FOR_STYLIST_MANAGER_ERROR });
    }
  };

export const getSalesForStylistManager =
  ({ managerPin, requestParams }) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.STYLIST_MANAGER_SALES_LOADING,
    });

    try {
      const stylistManagerRoute = `v1/stylistmanager/sales/metrics/associates?managerPin=${managerPin}`;
      const response = await associatePerformanceService.post(stylistManagerRoute, requestParams);

      dispatch({
        type: actionTypes.STYLIST_MANAGER_SALES_SUCCESS,
        payload: response?.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.STYLIST_MANAGER_SALES_ERROR,
        payload: error,
      });
    }
  };

export const clearSalesForStylistManager = () => ({
  type: actionTypes.CLEAR_STYLIST_MANAGER_SALES,
});

export const setStylistManagerSalesSaSearchTerm = (searchQuery) => ({
  type: actionTypes.STYLIST_MANAGER_SALES_SA_SEARCH_TERM,
  payload: searchQuery,
});

export const clearStylistManagerSalesSaSearchTerm = () => ({
  type: actionTypes.STYLIST_MANAGER_SALES_SA_SEARCH_TERM_CLEAR,
});

export const setIsBackFromAssociatePerfHomePage = (cachedCalanederAndFilterType) => ({
  type: actionTypes.STYLIST_MANAGER_SALES_SET_IS_BACK_FROM_ASSOCIATE_PERF_HOME_PAGE,
  payload: cachedCalanederAndFilterType,
});

export const clearIsBackFromAssociatePerfHomePage = () => ({
  type: actionTypes.STYLIST_MANAGER_SALES_SET_IS_BACK_FROM_ASSOCIATE_PERF_HOME_PAGE_CLEAR,
});

export const getStylistManagerClientConnects = (stylistManagerPin) => async (dispatch) => {
  dispatch({
    type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_LOADING,
  });

  try {
    const { data } = await stylistManagerClientConnectsService.get(`/fullMetrics?managerPin=${stylistManagerPin}`);
    dispatch({
      type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_ERROR,
      payload: error,
    });
  }
};

export const getStylistManagerClientConnectsDetails = (stylistManagerPin, requestParams) => async (dispatch) => {
  dispatch({
    type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_DETAILS_LOADING,
  });

  try {
    const { data } = await stylistManagerClientConnectsService.post(
      `/associatesMetrics?managerPin=${stylistManagerPin}`,
      requestParams,
    );

    dispatch({
      type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_DETAILS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_DETAILS_ERROR,
      payload: error,
    });
  }
};

export const clearStylistManagerClientConnectsDetails = () => async (dispatch) => {
  dispatch({
    type: actionTypes.STYLIST_MANAGER_CLIENT_CONNECTS_DETAILS_CLEAR,
  });
};
